import { Field } from 'module/api/field/types';
import { FieldObjectType } from 'module/api/field/types';
import { useTranslation } from 'module/hooks/use-translation';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { CenterAlignedBox } from 'module/ui/components/atoms/layout/layout';
import { useDeleteConfirmModal } from 'module/ui/components/atoms/modal/use-delete-confirm-modal';
import { MoreActionsDropdown } from 'module/ui/components/atoms/more-actions';
import { Switch } from 'module/ui/components/atoms/switch/switch';
import { faEdit, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { startCase } from 'lodash';
import { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  FieldRow as Row,
  FieldRowItem,
  Name,
  Description,
  MoreActionsRowItem,
} from './components';
import { FieldTypeWithIconFromField } from '../fields/field-types';

interface Props {
  field: Field;
  loading: boolean;
  changeEnabledStatus: (toStatus: boolean) => Promise<Field>;
  onDelete: () => void;
  onEdit: () => void;
}

const specialEmailFields = [
  'subject',
  'participants',
  'firstEmail',
  'latestReply',
  'body',
];

export const isSpecialEmailField = (fieldName: string): boolean => {
  return specialEmailFields.includes(fieldName);
};

export const canDisableField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldObjectType.Email:
      return isSpecialEmailField(fieldName);
    default:
      return false;
  }
};

export const canDeleteField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldObjectType.Email:
      return !isSpecialEmailField(fieldName);
    default:
      return true;
  }
};

export const canEditField = (field: Field): boolean => {
  const { objectType, fieldName } = field;

  switch (objectType) {
    case FieldObjectType.Email:
      return !isSpecialEmailField(fieldName);
    default:
      return true;
  }
};

export const FieldRow: FC<Props> = ({
  field,
  changeEnabledStatus,
  onEdit,
  onDelete,
}) => {
  const { isEnabled } = field;
  const [isFieldStatusUpdating, setFieldStatusUpdating] = useState(false);
  const onEnabledStatusSwitch = useCallback(() => {
    setFieldStatusUpdating(true);
    changeEnabledStatus(!isEnabled).finally(() => {
      setFieldStatusUpdating(false);
    });
  }, [changeEnabledStatus, isEnabled]);
  const { open: confirmDelete, dialog: deleteConfirmationDialog } =
    useDeleteConfirmModal({
      width: 476,
      title: useTranslation('settings.signal_fields.delete.confirm.title'),
      content: useTranslation(
        `settings.${field.objectType.toLowerCase()}.delete.confirm.message` as any,
      ),
      onOk: () => {
        onDelete();
      },
    });

  const disabled = canDisableField(field);

  // Build menu items based on permissions
  const menuItems: any[] = [];
  const children: any[] = [];

  if (canEditField(field)) {
    children.push({
      key: 'edit',
      label: <FormattedMessage id="edit" />,
      icon: <FontAwesomeIcon icon={faEdit} />,
    });
  }

  if (canDeleteField(field)) {
    children.push({
      key: 'delete',
      label: <FormattedMessage id="delete" />,
      icon: <FontAwesomeIcon icon={faTrash} />,
      danger: true,
    });
  }

  // Only show dropdown if there are actions available
  if (children.length > 0) {
    menuItems.push({
      type: 'group',
      label: <FormattedMessage id="more_actions" />,
      children,
    });
  }

  return (
    <>
      <Row>
        <FieldRowItem>
          <Name>{field.fieldLabel ?? startCase(field.fieldName)}</Name>
        </FieldRowItem>
        <FieldRowItem>
          <Description>{field.description}</Description>
        </FieldRowItem>
        <FieldRowItem>
          <FieldTypeWithIconFromField field={field} />
        </FieldRowItem>
        <FieldRowItem>
          <CenterAlignedBox>
            <Switch
              size="small"
              checked={isEnabled}
              onClick={onEnabledStatusSwitch}
              loading={isFieldStatusUpdating}
              disabled={disabled}
            ></Switch>
            <FormattedMessage id={isEnabled ? 'enabled' : 'disabled'} />
          </CenterAlignedBox>
        </FieldRowItem>
        {menuItems.length > 0 && (
          <MoreActionsRowItem>
            <MoreActionsDropdown
              menu={{
                onClick: ({ key }) => {
                  if (key === 'edit') {
                    onEdit();
                  } else if (key === 'delete') {
                    confirmDelete();
                  }
                },
                items: menuItems,
              }}
            />
          </MoreActionsRowItem>
        )}
      </Row>
      {deleteConfirmationDialog}
    </>
  );
};
